import { Text } from '@dixa/lib-shared-habla';
import _flatMap from 'lodash/flatMap';
import _groupBy from 'lodash/groupBy';
import _map from 'lodash/map';
import memoize from 'memoize-one';
import moment from 'moment';
import { memo, ReactElement } from 'react';

import { convertToHumanRelativeTime } from '../helpers/timeHelpers';

import type {
  FlowNodeOpeningHoursT,
  OpeningHoursNodeIntervalsT,
  OpeningHoursNodeScheduleT,
} from './nodeTypes/openingHours';
import { Day } from './types';

type GroupedDaysT = Record<string, OpeningHoursNodeScheduleT[]>;

const weekdays: readonly Day[] = moment
  .weekdays()
  .map((weekday) => Day[weekday]);

const getOpeningHoursStr = (
  intervals: OpeningHoursNodeIntervalsT[],
): string => {
  return (
    intervals.reduce((_pre: string, cur: OpeningHoursNodeIntervalsT) => {
      const open = convertToHumanRelativeTime(cur.open);
      const close = convertToHumanRelativeTime(cur.close);
      return `${open} - ${close}`;
    }, '') || 'Closed'
  );
};

const getGroupedDays = (
  schedule: OpeningHoursNodeScheduleT[],
): GroupedDaysT => {
  let index = 0;
  let currentKey = '';

  return _groupBy(schedule, (day: OpeningHoursNodeScheduleT): string => {
    const intervalStr: string = getOpeningHoursStr(day.intervals);
    const keyStr = `${index}-[${intervalStr}]`;
    if (keyStr !== currentKey) {
      index += 1;
      currentKey = `${index}-[${intervalStr}]`;
    }
    return currentKey;
  });
};

const parseTextFromPayload = memoize(
  (schedule: OpeningHoursNodeScheduleT[]): ReactElement[] => {
    const groupedDays: GroupedDaysT = getGroupedDays(schedule);
    const dates: ReactElement[] = _map(
      groupedDays,
      (days: OpeningHoursNodeScheduleT[], key: string): ReactElement => {
        let joinedDayNames: string = days
          .map((day: OpeningHoursNodeScheduleT): Day => {
            return weekdays[new Date(day.date).getDay()];
          })
          .join(', ');
        if (
          joinedDayNames ===
          `${Day.Monday}, ${Day.Tuesday}, ${Day.Wednesday}, ${Day.Thursday}, ${Day.Friday}`
        ) {
          joinedDayNames = 'Weekdays';
        }
        if (joinedDayNames === `${Day.Saturday}, ${Day.Sunday}`) {
          joinedDayNames = 'Weekends';
        }

        const openingHours: string = getOpeningHoursStr(days[0].intervals);
        return (
          <span key={key}>
            {joinedDayNames}: <strong>{openingHours}</strong>
          </span>
        );
      },
    );

    const commaSeparatedDates: ReactElement[] = _flatMap(
      dates,
      (date: ReactElement, i: number) => [date, <span key={i}>, </span>],
    );
    commaSeparatedDates.pop(); // remove last comma

    return commaSeparatedDates;
  },
);

const OpeningHoursFromPayload = ({
  node: {
    payload: { schedule },
  },
}: {
  node: FlowNodeOpeningHoursT;
}) => {
  return (
    <div style={{ height: '32px' }}>
      <Text.Paragraph clamp={2}>
        {parseTextFromPayload(schedule)}
      </Text.Paragraph>
    </div>
  );
};

export default memo(OpeningHoursFromPayload);
